import { useState, useMemo, useEffect, useCallback } from 'react';
import type { Farmer } from '../../../types';
import FarmerDetailSheet from './components/FarmerDetailSheet';
import { getFarmerById, getFarmersByHtx, getFarmerKyc, getDebtsByFarmer, getLimitsByFarmer, getContractsByFarmer, getLoansByFarmer, getFieldsByFarmer, approveFarmerMembership, rejectFarmerMembership, searchStandaloneFarmers, inviteFarmerToHtx, listProfileOnlyFarmers, mergeFarmerProfiles, suggestProfileMatches } from '../../../store';
import { StatusBadge } from '../../../components/base/StatusBadge';
import { formatCompactCurrency } from '../../../utils/format';
import { useStore } from '../../../hooks/useStore';
import { useAuth } from '../../../hooks/useAuth';
import { showToast } from '../../../components/base/Toast';
import { confirmModal } from '../../../components/base/ConfirmModal';
import { DEBT_STATUS, LOAN_STATUS } from '../../../utils/status';
import { getCreditScoreColor } from '../../../utils/status';
import { SearchFilterBar } from '@/components/base/SearchFilterBar';
import { CreateFarmerModal } from './components/CreateFarmerModal';

function InviteFarmerSearch({ htxId, sentInvites }: { htxId?: string; sentInvites: Farmer[] }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Farmer[]>([]);
  const [loading, setLoading] = useState(false);
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      searchStandaloneFarmers(query)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  async function handleInvite(farmerId: string) {
    if (!htxId) return;
    setInvitingId(farmerId);
    try {
      await inviteFarmerToHtx(farmerId, htxId);
      setInvitedIds((prev) => new Set(prev).add(farmerId));
      showToast('Đã gửi lời mời — chờ nông hộ xác nhận', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Mời thất bại', 'error');
    } finally {
      setInvitingId(null);
    }
  }

  return (
    <div>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Gõ tên hoặc SĐT nông hộ độc lập để tìm..."
        className="w-full h-10 px-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
      />
      <p className="text-xs text-slate-400 mt-1.5">Chỉ tìm được nông hộ đang hoạt động độc lập (chưa thuộc HTX nào).</p>
      {loading && <p className="text-xs text-slate-400 mt-3">Đang tìm...</p>}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {results.map((f) => (
          <div key={f.id} className="p-3 rounded-xl border border-slate-200 bg-white">
            <div className="flex items-start gap-2.5">
              <div className="w-9 h-9 rounded-full bg-agrida-100 flex items-center justify-center shrink-0">
                <span className="text-xs font-bold text-agrida-700">{f.name.charAt(0)}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 leading-tight truncate">{f.name}</p>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{f.phone}</p>
              </div>
            </div>
            <button
              type="button"
              disabled={invitingId === f.id || invitedIds.has(f.id)}
              onClick={() => handleInvite(f.id)}
              className="w-full mt-3 h-8 rounded-lg bg-agrida-700 text-white text-xs font-semibold disabled:opacity-50"
            >
              {invitedIds.has(f.id) ? 'Đã mời' : invitingId === f.id ? 'Đang mời...' : 'Mời tham gia'}
            </button>
          </div>
        ))}
        {!loading && query.trim().length >= 2 && results.length === 0 && (
          <div className="col-span-full bg-white rounded-xl border border-slate-200 p-8 text-center">
            <p className="text-sm text-slate-500">Không tìm thấy nông hộ độc lập nào phù hợp</p>
          </div>
        )}
      </div>

      {sentInvites.length > 0 && (
        <div className="mt-6">
          <p className="text-xs font-semibold text-slate-700 mb-2">Lời mời đã gửi, chờ nông hộ xác nhận ({sentInvites.length})</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {sentInvites.map((f) => (
              <div key={f.id} className="p-3 rounded-xl border border-slate-200 bg-white flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center shrink-0">
                  <span className="text-xs font-bold text-sky-700">{f.name.charAt(0)}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-900 leading-tight truncate">{f.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{f.phone}</p>
                </div>
                <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700">Đang chờ</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

type ProfileMatch = Farmer & { matchReasons: string[] };

/**
 * A self-registered farmer asking to join may be someone the HTX already
 * created a profile for (before they had an account). The likely matches —
 * by CCCD, phone, name — are looked up as soon as the request card shows, so
 * the HTX merges into that profile (one farmer, all data together) instead of
 * approving a second one. "Chọn hồ sơ khác" covers a match the heuristics miss.
 */
function MergeMatchPicker({ farmer, htxId }: { farmer: Farmer; htxId?: string }) {
  const [matches, setMatches] = useState<ProfileMatch[] | null>(null);
  const [merging, setMerging] = useState<string | null>(null);
  const [browsing, setBrowsing] = useState(false);
  const [query, setQuery] = useState('');
  const [browseResults, setBrowseResults] = useState<Farmer[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    suggestProfileMatches(farmer.id)
      .then((rows) => { if (!cancelled) setMatches(rows); })
      .catch(() => { if (!cancelled) setMatches([]); });
    return () => { cancelled = true; };
  }, [farmer.id]);

  useEffect(() => {
    if (!browsing || !htxId) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      listProfileOnlyFarmers(htxId, query.trim() || undefined)
        .then((rows) => { if (!cancelled) setBrowseResults(rows); })
        .catch(() => { if (!cancelled) setBrowseResults([]); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [browsing, query, htxId]);

  async function handleMerge(target: Farmer) {
    const ok = await confirmModal({
      title: 'Duyệt & gộp hồ sơ',
      message: `Gán tài khoản của ${farmer.name} vào hồ sơ "${target.name}" (${target.code}) do HTX đã tạo. Mùa vụ, thửa đất, nhật ký, hợp đồng nháp sẽ dồn về một hồ sơ duy nhất.`,
      confirmText: 'Duyệt & gộp',
      cancelText: 'Hủy',
      variant: 'primary',
    });
    if (!ok) return;
    setMerging(target.id);
    try {
      const result = await mergeFarmerProfiles(farmer.id, target.id, htxId);
      showToast(`Đã duyệt và gộp vào hồ sơ ${target.name}`, 'success');
      if (result.warning) {
        // confirmModal is a global portal, so the warning survives this card
        // unmounting once the pending list refreshes.
        await confirmModal({
          title: 'Cần rà soát sau khi gộp hồ sơ',
          message: result.warning,
          confirmText: 'Đã hiểu',
          cancelText: 'Đóng',
          variant: 'primary',
        });
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Gộp hồ sơ thất bại', 'error');
    } finally {
      setMerging(null);
    }
  }

  const row = (m: Farmer, reasons?: string[]) => (
    <div key={m.id} className="flex items-center justify-between gap-2 rounded-lg border border-sky-100 bg-white px-2.5 py-2">
      <div className="min-w-0">
        <p className="truncate text-xs font-semibold text-slate-800">{m.name} · {m.code}</p>
        <p className="truncate text-[11px] text-slate-500">{m.phone || '—'}{m.address ? ` · ${m.address}` : ''}</p>
        {reasons && reasons.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {reasons.map((r) => (
              <span key={r} className="rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">Trùng {r}</span>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        disabled={!!merging}
        onClick={() => handleMerge(m)}
        className="shrink-0 rounded-lg bg-sky-600 px-2.5 py-1.5 text-[11px] font-semibold text-white disabled:opacity-50"
      >
        {merging === m.id ? 'Đang gộp...' : 'Duyệt & gộp vào hồ sơ này'}
      </button>
    </div>
  );

  return (
    <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50 p-2.5">
      {matches === null && <p className="text-[11px] text-sky-700">Đang tìm hồ sơ HTX đã tạo trùng với nông hộ này...</p>}
      {matches && matches.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[11px] font-semibold text-sky-800">
            Có thể là nông hộ HTX đã tạo sẵn — nên gộp để dữ liệu về một hồ sơ:
          </p>
          {matches.map((m) => row(m, m.matchReasons))}
        </div>
      )}
      {matches && matches.length === 0 && !browsing && (
        <p className="text-[11px] text-slate-600">Không thấy hồ sơ HTX đã tạo trùng với nông hộ này — có thể duyệt như nông hộ mới.</p>
      )}

      {!browsing ? (
        <button type="button" onClick={() => setBrowsing(true)} className="mt-1.5 text-[11px] font-semibold text-sky-700 underline">
          Chọn hồ sơ khác do HTX đã tạo
        </button>
      ) : (
        <div className="mt-2 space-y-1.5">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Tìm theo tên, mã, SĐT..."
            className="h-8 w-full rounded-lg border border-sky-200 bg-white px-2 text-xs"
          />
          {browseResults === null && <p className="text-[11px] text-sky-700">Đang tải...</p>}
          {browseResults?.length === 0 && <p className="text-[11px] text-slate-500">Không có hồ sơ chưa liên kết tài khoản nào khớp.</p>}
          {browseResults?.map((m) => row(m))}
        </div>
      )}
    </div>
  );
}

function JoinRequestCard({ farmer, htxId }: { farmer: Farmer; htxId?: string }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  async function handleApprove() {
    setBusy(true);
    try {
      await approveFarmerMembership(farmer.id, htxId);
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    setBusy(true);
    try {
      await rejectFarmerMembership(farmer.id, reason, htxId);
      setRejecting(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-3 rounded-xl border border-slate-200 bg-white">
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-full bg-agrida-100 flex items-center justify-center shrink-0">
          <span className="text-xs font-bold text-agrida-700">{farmer.name.charAt(0)}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 leading-tight truncate">{farmer.name}</p>
          <p className="text-xs text-slate-500 mt-0.5 truncate">{farmer.phone} · {farmer.loaiCay || 'Chưa rõ'}</p>
        </div>
      </div>

      {/* Phase 2: this pending farmer might duplicate a profile HTX already
          pre-created (Phase 1) — suggested up front, merge instead of approving
          a second profile for the same person. */}
      <MergeMatchPicker farmer={farmer} htxId={htxId} />

      {!rejecting ? (
        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            disabled={busy}
            onClick={handleApprove}
            className="flex-1 h-8 rounded-lg bg-agrida-700 text-white text-xs font-semibold disabled:opacity-50"
          >
            Duyệt là nông hộ mới
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setRejecting(true)}
            className="flex-1 h-8 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold disabled:opacity-50"
          >
            Từ chối
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Lý do từ chối (không bắt buộc)"
            className="w-full h-8 px-2 rounded-lg border border-slate-200 text-xs"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={handleReject}
              className="flex-1 h-8 rounded-lg bg-red-600 text-white text-xs font-semibold disabled:opacity-50"
            >
              Xác nhận từ chối
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setRejecting(false)}
              className="flex-1 h-8 rounded-lg border border-slate-200 text-slate-600 text-xs font-semibold disabled:opacity-50"
            >
              Hủy
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function getDebtSummary(farmerId: string): { label: string; hasOverdue: boolean; hasDueSoon: boolean; hasActive: boolean } {
  const debts = getDebtsByFarmer(farmerId).filter((d) => d.conNo > 0);
  if (debts.length === 0) return { label: 'Không nợ', hasOverdue: false, hasDueSoon: false, hasActive: false };
  if (debts.some((d) => d.status === DEBT_STATUS.OVERDUE)) return { label: 'Quá hạn', hasOverdue: true, hasDueSoon: false, hasActive: false };
  if (debts.some((d) => d.status === DEBT_STATUS.DUE_SOON || d.status === DEBT_STATUS.DUE)) return { label: 'Sắp đến hạn', hasOverdue: false, hasDueSoon: true, hasActive: false };
  if (debts.some((d) => d.status === DEBT_STATUS.ACTIVE || d.status === DEBT_STATUS.PENDING_PAYMENT)) return { label: 'Đang vay', hasOverdue: false, hasDueSoon: false, hasActive: true };
  return { label: 'Không nợ', hasOverdue: false, hasDueSoon: false, hasActive: false };
}

function FarmerCard({ farmer, isSelected, onClick }: { farmer: Farmer; isSelected: boolean; onClick: () => void }) {
  const kyc = getFarmerKyc(farmer);
  const fields = getFieldsByFarmer(farmer.id);
  const debts = getDebtsByFarmer(farmer.id);
  const limits = getLimitsByFarmer(farmer.id);
  const totalArea = fields.reduce((sum, fi) => sum + fi.area, 0);
  const totalNo = debts.filter((d) => d.conNo > 0).reduce((sum, d) => sum + d.conNo, 0);
  const hanMucTong = limits.reduce((sum, l) => sum + l.hanMuc, 0);
  const daDung = limits.reduce((sum, l) => sum + l.daSuDung, 0);
  const conLai = hanMucTong - daDung;
  const pendingLoans = getLoansByFarmer(farmer.id).filter((l) => l.status === LOAN_STATUS.PENDING_CHECK || l.status === LOAN_STATUS.NEED_MORE);
  const debtSummary = getDebtSummary(farmer.id);
  const isLocked = kyc.kycStatus !== 'ekyc_verified';
  const isHighRisk = kyc.riskLevel === 'Nguy cơ';
  const scoreColor = getCreditScoreColor(kyc.creditScore);

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={isSelected ? 'farmer-detail-active' : 'farmer-detail-button'}
      className={`w-full text-left p-3 rounded-xl border transition-colors ${isSelected ? 'border-agrida-700 bg-agrida-50/50 ring-1 ring-agrida-700' : 'border-slate-200 bg-white hover:border-agrida-300'}`}
    >
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-full bg-agrida-100 flex items-center justify-center shrink-0">
          <span className="text-xs font-bold text-agrida-700">{farmer.name.charAt(0)}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-semibold text-slate-900 leading-tight truncate">{farmer.name}</p>
            <span className="text-xs text-slate-400 shrink-0">{farmer.code}</span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5 truncate">{farmer.phone} · {farmer.loaiCay} · {totalArea.toFixed(1)}ha</p>
        </div>
      </div>

      {/* Mini indicators */}
      <div className="flex flex-wrap items-center gap-1 mt-2">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${isLocked ? 'bg-slate-100 text-slate-500' : 'bg-agrida-100 text-agrida-700'}`}>
          {isLocked ? 'Chưa eKYC' : 'eKYC OK'}
        </span>
        {kyc.creditScore && (
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${scoreColor === 'text-agrida-600' ? 'bg-agrida-100 text-agrida-700' : scoreColor === 'text-amber-600' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
            {kyc.creditScore}
          </span>
        )}
        {debtSummary.hasOverdue && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Quá hạn</span>}
        {pendingLoans.length > 0 && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700">{pendingLoans.length} y/c vay</span>}
        {farmer.membershipStatus === 'Từ chối' && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-600">Đã từ chối gia nhập</span>}
      </div>

      {/* Quick numbers */}
      <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 mt-2 text-xs">
        <div><span className="text-slate-400">Hạn mức còn:</span> <span className={`font-medium ${hanMucTong > 0 ? 'text-slate-700' : 'text-slate-400'}`}>{hanMucTong > 0 ? formatCompactCurrency(conLai) : '—'}</span></div>
        <div><span className="text-slate-400">Dư nợ:</span> <span className={`font-medium ${totalNo > 0 ? 'text-red-600' : 'text-slate-400'}`}>{totalNo > 0 ? formatCompactCurrency(totalNo) : '0'}</span></div>
      </div>
    </button>
  );
}

export default function HtxFarmerPage() {
  useStore();
  const { user } = useAuth();
  const allFarmers = getFarmersByHtx(user?.htxId);
  // HTX-initiated invites are awaiting the FARMER's own response, not this HTX's —
  // they must never show up in "Yêu cầu gia nhập" as something for the HTX to approve.
  const pendingFarmers = useMemo(() => allFarmers.filter((f) => f.membershipStatus === 'Chờ xác nhận' && f.membershipInitiatedBy !== 'HTX'), [allFarmers]);
  const sentInvites = useMemo(() => allFarmers.filter((f) => f.membershipStatus === 'Chờ xác nhận' && f.membershipInitiatedBy === 'HTX'), [allFarmers]);
  const farmers = useMemo(() => allFarmers.filter((f) => f.membershipStatus !== 'Chờ xác nhận'), [allFarmers]);
  const [tab, setTab] = useState<'roster' | 'requests' | 'invite'>('roster');
  const [selectedFarmer, setSelectedFarmer] = useState<Farmer | null>(null);
  // The click captures a snapshot; edits made inside the sheet (e.g. attaching
  // a document) refresh the store, not that snapshot. Reading the store's copy
  // keeps the sheet current — the "Còn thiếu" count no longer lags behind a
  // document that was just uploaded.
  const liveSelectedFarmer = selectedFarmer ? (getFarmerById(selectedFarmer.id) ?? selectedFarmer) : null;
  const [sheetOpen, setSheetOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const stats = useMemo(() => {
    const total = farmers.length;
    const verified = farmers.filter((f) => getFarmerKyc(f).kycStatus === 'ekyc_verified').length;
    const pending = farmers.filter((f) => getFarmerKyc(f).kycStatus === 'ekyc_pending').length;
    const needMore = farmers.filter((f) => getFarmerKyc(f).kycStatus === 'ekyc_need_more').length;
    const failed = farmers.filter((f) => getFarmerKyc(f).kycStatus === 'ekyc_failed').length;
    const notKyc = farmers.filter((f) => getFarmerKyc(f).kycStatus === 'registered').length;
    const totalFields = farmers.reduce((sum, f) => sum + getFieldsByFarmer(f.id).length, 0);
    const totalArea = farmers.reduce((sum, f) => sum + getFieldsByFarmer(f.id).reduce((s, fi) => s + fi.area, 0), 0);
    return { total, verified, pending, needMore, failed, notKyc, totalFields, totalArea };
  }, [farmers]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return farmers.filter((farmer) => {
      const matchesSearch = !s || farmer.name.toLowerCase().includes(s) || farmer.code.toLowerCase().includes(s) || farmer.phone.includes(s);
      if (!matchesSearch) return false;
      const kyc = getFarmerKyc(farmer);
      if (statusFilter === 'verified') return kyc.kycStatus === 'ekyc_verified';
      if (statusFilter === 'pending') return kyc.kycStatus === 'ekyc_pending';
      if (statusFilter === 'need_more') return kyc.kycStatus === 'ekyc_need_more';
      if (statusFilter === 'overdue') return getDebtSummary(farmer.id).hasOverdue;
      return true;
    });
  }, [farmers, search, statusFilter]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ── Farmer list (full width) ── */}
      <div className="flex flex-col w-full h-[calc(100dvh-120px)] lg:h-[calc(100vh-64px)] overflow-hidden">
        <div className="px-4 pt-4 pb-3 shrink-0">
          <h2 className="text-lg font-bold text-slate-900">Quản lý nông hộ</h2>

          {/* flex-wrap + whitespace-nowrap: with four Vietnamese labels on a phone the
              row could not fit, so the tabs were squeezed and their text wrapped to
              two or three lines inside an h-8 pill — the label spilled past the
              button's border. Wrapping the row instead keeps every label on one line. */}
          <div className="flex flex-wrap items-center gap-1 mt-3 p-1 bg-slate-100 rounded-lg w-fit max-w-full">
            <button
              type="button"
              onClick={() => setTab('roster')}
              className={`px-3 h-8 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ${tab === 'roster' ? 'bg-white text-agrida-700 shadow-sm' : 'text-slate-500'}`}
            >
              Danh sách nông hộ
            </button>
            <button
              type="button"
              onClick={() => setTab('requests')}
              className={`px-3 h-8 rounded-md text-xs font-semibold whitespace-nowrap transition-colors relative ${tab === 'requests' ? 'bg-white text-agrida-700 shadow-sm' : 'text-slate-500'}`}
            >
              Yêu cầu gia nhập
              {pendingFarmers.length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                  {pendingFarmers.length}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setTab('invite')}
              className={`px-3 h-8 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ${tab === 'invite' ? 'bg-white text-agrida-700 shadow-sm' : 'text-slate-500'}`}
            >
              Mời nông hộ độc lập
            </button>
          </div>

          {tab === 'roster' && (
            <>
              <div className="mt-3">
                <SearchFilterBar
                  value={search}
                  onChange={setSearch}
                  placeholder="Tìm tên, mã, SĐT..."
                  selected={statusFilter}
                  onSelect={setStatusFilter}
                  options={[
                    { value: '', label: 'Tất cả', count: stats.total },
                    { value: 'verified', label: 'Đã xác thực eKYC', count: stats.verified, dotClass: 'bg-agrida-500' },
                    { value: 'pending', label: 'Chờ xác thực', count: stats.pending, dotClass: 'bg-sky-500' },
                    { value: 'need_more', label: 'Cần bổ sung', count: stats.needMore, dotClass: 'bg-amber-500' },
                    { value: 'overdue', label: 'Có nợ quá hạn', dotClass: 'bg-rose-500' },
                  ]}
                />
              </div>
              <p className="text-xs text-slate-400 mt-1.5">{filtered.length} nông hộ</p>
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="mt-3 w-full rounded-xl border border-dashed border-agrida-300 bg-agrida-50 py-2.5 text-sm font-semibold text-agrida-700"
              >
                + Tạo hồ sơ nông hộ mới
              </button>
            </>
          )}
          {tab === 'requests' && (
            <p className="text-xs text-slate-400 mt-3">{pendingFarmers.length} yêu cầu đang chờ xác nhận</p>
          )}
        </div>

        {/* Content scroll area */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {tab === 'roster' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {filtered.map((f) => (
                <FarmerCard
                  key={f.id}
                  farmer={f}
                  isSelected={selectedFarmer?.id === f.id}
                  onClick={() => { setSelectedFarmer(f); setSheetOpen(true); }}
                />
              ))}
              {filtered.length === 0 && (
                <div className="col-span-full bg-white rounded-xl border border-slate-200 p-8 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-slate-100 flex items-center justify-center"><i className="ri-user-search-line text-xl text-slate-400" /></div>
                  <p className="text-sm text-slate-500">Không tìm thấy nông hộ</p>
                </div>
              )}
            </div>
          )}
          {tab === 'requests' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {pendingFarmers.map((f) => (
                <JoinRequestCard key={f.id} farmer={f} htxId={user?.htxId} />
              ))}
              {pendingFarmers.length === 0 && (
                <div className="col-span-full bg-white rounded-xl border border-slate-200 p-8 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-slate-100 flex items-center justify-center"><i className="ri-user-search-line text-xl text-slate-400" /></div>
                  <p className="text-sm text-slate-500">Không có yêu cầu gia nhập nào</p>
                </div>
              )}
            </div>
          )}
          {tab === 'invite' && <InviteFarmerSearch htxId={user?.htxId} sentInvites={sentInvites} />}
        </div>
      </div>

      {createOpen && (
        <CreateFarmerModal
          htxId={user?.htxId}
          onClose={() => setCreateOpen(false)}
          onCreated={() => setCreateOpen(false)}
        />
      )}

      {/* ── Farmer detail sheet (all devices) ── */}
      {liveSelectedFarmer && (
        <FarmerDetailSheet
          farmer={liveSelectedFarmer}
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </div>
  );
}
